function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { extendTheme as extendChakraTheme } from "@chakra-ui/react";
import { ThemedSkeleton } from "../feedback";
import { ThemedMenu, ThemedModal, ThemedPopover, ThemedTooltip, ThemedDrawer } from "../overlay";
import { ThemedInput, ThemedNumberInput, ThemedButton, ThemedCloseButton, ThemedCheckbox, ThemedRadio, ThemedSelect, ThemedSwitch, ThemedTextarea, ThemedFormControl, ThemedPinInput, ThemedFormError, ThemedSlider } from "../form";
import { ThemedAccordion } from "../disclosure";
import { ThemedAvatar, ThemedIcon } from "../medias";
import { ThemedLink } from "../navigation";
import { ThemedHeading } from "../typography";
import { ThemedText } from "../typography";
import { ThemedContainer, ThemedBadge } from "../layout";
import { ThemedTable, ThemedList, ThemedLabel } from "../data-display";
import { ThemedProgress } from "../feedback";
import semanticTokens from "./semantic-tokens/index";
import coreColors from "./foundations/colors";
import legacyColors from "./foundations/legacy-colors";
import scales from "./foundations/scales";
/**
 * Important to specify all breakpoints (xl & 2xl same thing for us)
 * Otherwise, useBreakpointValue infinite loops
 * See: https://github.com/chakra-ui/chakra-ui/issues/3042#issuecomment-771009839
 */ const breakpoints = {
    sm: '724px',
    md: '1024px',
    lg: '1200px',
    xl: '1535px',
    '2xl': '1800px'
};
// TODO: remove me from inner theme usage and codemod to remove them from consuming projects
const oldSemanticTokens = {
    colors: {
        'text.main': semanticTokens.colors['text.neutral.primary.default'],
        'text.secondary': semanticTokens.colors['text.neutral.secondary.default'],
        'text.inverse': semanticTokens.colors['text.neutral.inverse.default'],
        'accent.main': semanticTokens.colors['background.accent.primary.default'],
        'accent.strong': semanticTokens.colors['background.accent.primary.hovered'],
        'accent.weak': semanticTokens.colors['background.accent.primary.disabled'],
        'neutral.main': semanticTokens.colors['background.neutral.primary.default'],
        'neutral.secondary': semanticTokens.colors['background.neutral.secondary.default'],
        'neutral.strong': semanticTokens.colors['background.neutral.primary.hovered'],
        'neutral.weak': semanticTokens.colors['background.neutral.primary.disabled'],
        'bg.main': semanticTokens.colors['background.neutral.primary.default'],
        'bg.secondary': semanticTokens.colors['background.neutral.secondary.default'],
        'bg.contrast': semanticTokens.colors['background.neutral.secondary.pressed'],
        'bg.inverse': semanticTokens.colors['background.neutral.inverse.default'],
        'divider.main': semanticTokens.colors['divider.neutral.primary.default'],
        'intent.error': semanticTokens.colors['text.feedback.error.default'],
        'intent.success': semanticTokens.colors['text.feedback.success.default'],
        'intent.warning': semanticTokens.colors['text.feedback.warning.default']
    }
};
const theme = {
    styles: {
        global: {
            body: {
                fontFamily: 'body',
                color: 'text.neutral.primary.default',
                bg: 'background.neutral.primary.default',
                lineHeight: 'base',
                fontSize: scales[1]
            },
            '*::placeholder': {
                color: 'neutral.80',
                _dark: {
                    color: 'neutral.100'
                }
            },
            '*, *::before, &::after': {
                borderColor: 'divider.neutral.primary.default',
                wordWrap: 'break-word'
            },
            ':focus-visible': {
                outlineColor: 'border.accent.primary.default'
            }
        }
    },
    // TODO: This palette's usage is solely for component's themes to be able to convert chakra's hunderds increments to our ten increments
    colors: {
        primary: {
            50: coreColors.purple['50'],
            100: coreColors.purple['60'],
            200: coreColors.purple['70'],
            300: coreColors.purple['80'],
            400: coreColors.purple['90'],
            500: coreColors.purple['100'],
            600: coreColors.purple['110'],
            700: coreColors.purple['120'],
            800: coreColors.purple['130'],
            900: coreColors.purple['140']
        },
        global: {
            // to remove? ping @lgalliot
            color: [
                'black',
                'white'
            ],
            bg: [
                'red',
                'red'
            ]
        },
        gray: {
            50: coreColors.neutral['00'],
            100: coreColors.neutral['20'],
            200: coreColors.neutral['40'],
            300: coreColors.neutral['60'],
            400: coreColors.neutral['80'],
            500: coreColors.neutral['100'],
            600: coreColors.neutral['120'],
            700: coreColors.neutral['140'],
            800: coreColors.neutral['170'],
            900: coreColors.neutral['200']
        },
        neutral: {
            50: coreColors.neutral['00'],
            100: coreColors.neutral['20'],
            200: coreColors.neutral['40'],
            300: coreColors.neutral['60'],
            400: coreColors.neutral['80'],
            500: coreColors.neutral['100'],
            600: coreColors.neutral['120'],
            700: coreColors.neutral['140'],
            800: coreColors.neutral['170'],
            900: coreColors.neutral['200']
        },
        default: {
            50: coreColors.neutral['00'],
            100: coreColors.neutral['20'],
            200: coreColors.neutral['40'],
            300: coreColors.neutral['60'],
            400: coreColors.neutral['80'],
            500: coreColors.neutral['100'],
            600: coreColors.neutral['120'],
            700: coreColors.neutral['140'],
            800: coreColors.neutral['170'],
            900: coreColors.neutral['200']
        },
        // Allows to use colorScheme="white" and "black" without issues
        white: _object_spread({
            60: '#fff',
            70: '#fff',
            80: '#fff',
            90: '#fff',
            110: '#fff',
            120: '#fff',
            130: '#fff',
            140: '#fff',
            150: '#fff'
        }, legacyColors.white),
        black: _object_spread({
            60: '#000',
            70: '#000',
            80: '#000',
            90: '#000',
            110: '#000',
            120: '#000',
            130: '#000',
            140: '#000',
            150: '#000'
        }, legacyColors.black)
    },
    config: {
        cssVarPrefix: 'tempo'
    },
    components: {
        Accordion: ThemedAccordion,
        Badge: ThemedBadge,
        Button: ThemedButton,
        CloseButton: ThemedCloseButton,
        Popover: ThemedPopover,
        Input: ThemedInput,
        Icon: ThemedIcon,
        Modal: ThemedModal,
        Radio: ThemedRadio,
        Checkbox: ThemedCheckbox,
        Select: ThemedSelect,
        Switch: ThemedSwitch,
        NumberInput: ThemedNumberInput,
        Textarea: ThemedTextarea,
        FormControl: ThemedFormControl,
        FormError: ThemedFormError,
        Menu: ThemedMenu,
        Tooltip: ThemedTooltip,
        Drawer: ThemedDrawer,
        Link: ThemedLink,
        Container: ThemedContainer,
        Heading: ThemedHeading,
        Text: ThemedText,
        Table: ThemedTable,
        Progress: ThemedProgress,
        List: ThemedList,
        Skeleton: ThemedSkeleton,
        PinInput: ThemedPinInput,
        Label: ThemedLabel,
        Avatar: ThemedAvatar,
        Slider: ThemedSlider
    },
    breakpoints,
    letterSpacings: {
        tighter: '-0.05em',
        tight: '-0.025em',
        normal: '0',
        wide: '0.025em',
        wider: '0.05em',
        widest: '0.1em'
    },
    lineHeights: {
        normal: 'normal',
        none: 1,
        shorter: 1.25,
        short: 1.375,
        base: 1.5,
        tall: 1.625,
        taller: '2',
        '3': '.75rem',
        '4': '1rem',
        '5': '1.25rem',
        '6': '1.5rem',
        '7': '1.75rem',
        '8': '2rem',
        '9': '2.25rem',
        '10': '2.5rem'
    },
    fontWeights: {
        hairline: 100,
        thin: 200,
        light: 300,
        normal: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
        extrabold: 800,
        black: 900
    },
    fontSizes: {
        xs: '0.75rem',
        sm: '0.875rem',
        md: '1rem',
        lg: '1.125rem',
        xl: '1.25rem',
        '2xl': '1.5rem',
        '3xl': '1.875rem',
        '4xl': '2.25rem',
        '5xl': '3rem',
        '6xl': '3.75rem',
        '7xl': '4.5rem',
        '8xl': '6rem',
        '9xl': '8rem'
    },
    transition: {
        'property.common': 'background-color,border-color,outline-color,color,fill,stroke,opacity,box-shadow,transform',
        'property.colors': 'background-color,border-color,outline-color,color,fill,stroke'
    },
    fonts: {
        body: 'Inter, Arial, sans-serif',
        heading: '"Deezer Product", Tahoma, Arial, sans-serif',
        mono: `SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace`
    },
    semanticTokens: _object_spread_props(_object_spread({}, semanticTokens), {
        colors: _object_spread({}, semanticTokens.colors, oldSemanticTokens.colors)
    })
};
const extendTheme = (...extensions)=>extendChakraTheme(theme, ...extensions);
/**
 * @deprecated - it's the same as the regular extendTheme use it instead
 * @param extensions
 * @returns
 */ const extendTheme2023 = extendTheme;
export { theme, extendTheme, extendTheme2023 };
// For the demo
export { default as corePalette } from "./foundations/colors";
export * from "./app-extensions/zen/index";

//# sourceMappingURL=index.js.map